import React, { useCallback, useEffect } from 'react';
import type { PageDetailOut } from '../../types/catalog';
import { AudioButton } from '../components/AudioButton';
import { Volume2 } from 'lucide-react';

interface ReadListenV1Props {
  page: PageDetailOut;
  onComplete?: () => void;
}

/** Split text by newlines into tokens for chip/grid display */
function tokensFromBlocks(blocks: Array<{ type?: string; text?: string }>): string[] {
  const all: string[] = [];
  for (const b of blocks) {
    const text = b.text?.trim();
    if (!text) continue;
    const parts = text.split(/\n+/).filter(Boolean);
    all.push(...parts);
  }
  return all;
}

export const ReadListenV1: React.FC<ReadListenV1Props> = ({ page, onComplete }) => {
  const blocks = page.page_json_v1?.content?.blocks ?? [];
  const tokens = tokensFromBlocks(blocks);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.getVoices();
    }
  }, []);

  const playAll = useCallback(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const speakNext = (i: number) => {
      if (i >= tokens.length) return;
      const u = new SpeechSynthesisUtterance(tokens[i]);
      u.lang = 'ta-IN';
      const voices = window.speechSynthesis.getVoices();
      const tamil = voices.find((v) => v.lang.startsWith('ta'));
      if (tamil) u.voice = tamil;
      u.onend = () => setTimeout(() => speakNext(i + 1), 400);
      window.speechSynthesis.speak(u);
    };
    speakNext(0);
  }, [tokens]);

  const showChips = tokens.length > 1 || (tokens.length === 1 && tokens[0].length <= 20);

  return (
    <div className="space-y-6 p-6 overflow-auto">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
        {page.page_title || 'Read & Listen'}
      </h1>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={playAll}
          disabled={tokens.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium hover:bg-primary-100 dark:hover:bg-primary-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Volume2 className="w-5 h-5" />
          <span>Play All</span>
        </button>
      </div>

      <div className={showChips ? 'flex flex-wrap gap-3' : 'space-y-4'}>
        {tokens.map((token, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 ${
              showChips
                ? 'px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                : ''
            }`}
          >
            <span
              className={
                showChips
                  ? 'text-2xl font-semibold text-gray-900 dark:text-white'
                  : 'text-base text-gray-700 dark:text-gray-300 whitespace-pre-line'
              }
            >
              {token}
            </span>
            <AudioButton text={token} label={`Play: ${token}`} size="sm" />
          </div>
        ))}
      </div>

      {tokens.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400">No content</p>
      )}
    </div>
  );
};
